import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Calculator, ShoppingCart, FileText, Briefcase, ArrowRight, Shield, Clock, Award, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
const Homepage = () => {
  const features = [
    {
      icon: Calculator,
      title: 'Calculator Preț',
      description: 'Calculează costul lucrărilor de izolații în timp real',
      link: '/calculator',
      color: 'from-cyan-500 to-blue-500',
      testId: 'feature-calculator'
    },
    {
      icon: ShoppingCart,
      title: 'Magazin',
      description: 'Comandă materiale de calitate direct de la noi',
      link: '/magazin',
      color: 'from-lime-500 to-green-500',
      testId: 'feature-shop'
    },
    {
      icon: Wrench,
      title: 'Servicii',
      description: 'Servicii profesionale de hidroizolații și termoizolații',
      link: '/servicii',
      color: 'from-amber-500 to-orange-500',
      testId: 'feature-services'
    },
    {
      icon: Briefcase,
      title: 'Portofoliu',
      description: 'Vezi proiectele noastre finalizate și clienții mulțumiți',
      link: '/portofoliu',
      color: 'from-purple-500 to-pink-500',
      testId: 'feature-portfolio'
    }
  ];

  const benefits = [
    { icon: Shield, title: 'Garanție Extinsă', description: 'Până la 15 ani în funcție de sistemul ales' },
    { icon: Clock, title: 'Livrare Rapidă', description: 'Execuție rapidă și profesionistă' },
    { icon: Award, title: 'Calitate Premium', description: 'Materiale certificate și echipă experimentată' }
  ];

  return (
    <div className="min-h-screen" data-testid="homepage">
      <SEO canonical="/" />
      <Navbar />

      <div className="pt-16">
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1726589004565-bedfba94d3a2?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/80 to-slate-900/80 backdrop-blur-sm"></div>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative z-10 text-center px-4 max-w-5xl mx-auto"
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="hero-title">
              IzoMAG
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl text-cyan-100 mb-4" data-testid="hero-slogan">
              La noi găsești soluția corectă la prețul corect
            </p>
            <p className="text-lg text-white/80 mb-12 max-w-2xl mx-auto">
              Sistem complet de izolații pentru acoperșuri, fundații și balcoane. Calitate garantată, prețuri transparente.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/calculator" className="btn-primary text-lg px-8 py-4" data-testid="hero-calculator-btn">
                Calculează Oferta <ArrowRight className="inline ml-2" />
              </Link>
              <Link to="/portofoliu" className="btn-secondary text-lg px-8 py-4" data-testid="hero-portfolio-btn">
                Vezi Portofoliul
              </Link>
            </div>
          </motion.div>
        </section>

        <section className="py-20 px-4 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Serviciile Noastre
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Oferim soluții complete pentru toate nevoile tale de izolații
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <Link to={feature.link} data-testid={feature.testId}>
                    <div className="glass-card p-8 h-full hover:shadow-2xl transition-all">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 floating-animation`}>
                        <feature.icon className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {feature.title}
                      </h3>
                      <p className="text-slate-600 mb-4">{feature.description}</p>
                      <div className="text-orange-500 font-semibold flex items-center">
                        Accesează <ArrowRight className="ml-2 w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 px-4 bg-gradient-to-br from-cyan-50 to-lime-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="glass-card p-8 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <footer className="bg-slate-900 text-white py-12 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <div className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>IzoMAG</div>
            <p className="text-slate-400 mb-6">La noi găsești soluția corectă la prețul corect</p>
            <p className="text-sm text-slate-500">© 2024 IzoMAG. Toate drepturile rezervate.</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Homepage;
